import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Trophy } from "lucide-react";

const MONTHS_IT = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const LOAD_TONE = { Scarico: "bg-emerald-200", Basso: "bg-zinc-200", Medio: "bg-blue-200", "Medio-Alto": "bg-blue-400", Alto: "bg-blue-700" };
const day = (s) => new Date(s.slice(0, 10) + "T00:00:00");
const fmt = (s) => day(s).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" });

export default function SeasonTimeline({ groupId, currentWeek, onSelectWeek }) {
  const [d, setD] = useState(null);
  useEffect(() => { if (groupId) api.get("/programming/season", { params: { group_id: groupId } }).then((r) => setD(r.data)); }, [groupId]);

  const model = useMemo(() => {
    if (!d) return null;
    const start = day(d.season.start_date); const end = day(d.season.end_date);
    const total = Math.max(1, (end - start) / 864e5);
    const pos = (s) => Math.min(100, Math.max(0, ((day(s) - start) / 864e5 / total) * 100));
    const span = (a, b) => ({ left: `${pos(a || d.season.start_date)}%`, width: `${Math.max(1.2, pos(b || d.season.end_date) - pos(a || d.season.start_date))}%` });
    const months = []; const m = new Date(start.getFullYear(), start.getMonth(), 1);
    while (m <= end) { months.push({ label: MONTHS_IT[m.getMonth()], left: pos(m.toISOString().slice(0, 10)) }); m.setMonth(m.getMonth() + 1); }
    const weeks = []; const w = new Date(start); w.setDate(w.getDate() - ((w.getDay() + 6) % 7));
    const byWeek = Object.fromEntries((d.weeks || []).map((x) => [x.week_start, x]));
    const microByWeek = Object.fromEntries((d.microcycles || []).map((x) => [x.week_start, x]));
    while (w <= end) { const ws = w.toISOString().slice(0, 10); weeks.push({ ws, left: pos(ws), micro: microByWeek[ws], stats: byWeek[ws] }); w.setDate(w.getDate() + 7); }
    const maxPlanned = Math.max(1, ...weeks.map((x) => x.stats?.planned || 0));
    return { pos, span, months, weeks, maxPlanned, weekW: 100 / Math.max(1, weeks.length) };
  }, [d]);

  if (!d || !model) return <div className="text-zinc-500 py-10 text-center">Caricamento stagione…</div>;
  const { span, months, weeks, maxPlanned, weekW, pos } = model;
  const totPlanned = (d.weeks || []).reduce((s, x) => s + x.planned, 0);

  return (
    <section className="space-y-3" data-testid="season-timeline">
      <div className="flex flex-wrap items-baseline gap-3 text-sm">
        <span className="font-heading text-2xl font-bold">{d.season.name || "Stagione"}</span>
        <span className="font-mono text-zinc-500">{fmt(d.season.start_date)} → {fmt(d.season.end_date)}</span>
        <span className="text-zinc-500" data-testid="season-totals">{d.macrocycles.length} macro · {d.mesocycles.length} meso · {d.microcycles.length} micro · {totPlanned} sedute · {d.competitions.length} gare</span>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md p-4 overflow-x-auto">
        <div className="min-w-[900px] space-y-1.5">
          <div className="relative h-5 text-[10px] uppercase tracking-wider text-zinc-400 border-b border-zinc-100">
            {months.map((m, i) => <span key={i} className="absolute" style={{ left: `${m.left}%` }}>{m.label}</span>)}
          </div>

          <Row label="Macro">
            {d.macrocycles.map((mc) => <Bar key={mc.id} style={span(mc.start_date, mc.end_date)} cls="bg-zinc-900 text-white" title={`${mc.name}${mc.objective ? " · " + mc.objective : ""}`} testId={`season-macro-${mc.id}`}>{mc.name}</Bar>)}
            {d.macrocycles.length === 0 && <Empty>Nessun macrociclo</Empty>}
          </Row>
          <Row label="Meso">
            {d.mesocycles.map((ms) => <Bar key={ms.id} style={span(ms.start_date, ms.end_date)} cls="bg-[#0052FF] text-white" title={`${ms.name}${ms.primary_objective ? " · " + ms.primary_objective : ""}`} testId={`season-meso-${ms.id}`}>{ms.name}</Bar>)}
            {d.mesocycles.length === 0 && <Empty>Nessun mesociclo</Empty>}
          </Row>
          <Row label="Micro" h="h-8">
            {weeks.map((wk) => (
              <button key={wk.ws} data-testid={`season-week-${wk.ws}`} onClick={() => onSelectWeek?.(wk.ws)} title={`${fmt(wk.ws)}${wk.micro ? " · " + (wk.micro.primary_objective || "") + " · carico " + wk.micro.planned_load : " · nessun microciclo"}`}
                className={`absolute top-0 h-full border-r border-white rounded-sm transition-colors hover:ring-2 hover:ring-black ${wk.micro ? LOAD_TONE[wk.micro.planned_load] || "bg-blue-200" : "bg-zinc-100"} ${wk.ws === currentWeek ? "ring-2 ring-black" : ""}`}
                style={{ left: `${wk.left}%`, width: `${weekW}%` }} />
            ))}
          </Row>
          <Row label="Sedute" h="h-10">
            {weeks.map((wk) => wk.stats ? (
              <button key={wk.ws} onClick={() => onSelectWeek?.(wk.ws)} title={`${fmt(wk.ws)} · ${wk.stats.planned} sedute (${wk.stats.completed} svolte)`} data-testid={`season-sessions-${wk.ws}`}
                className="absolute bottom-0 flex flex-col justify-end" style={{ left: `${wk.left}%`, width: `${weekW}%` }}>
                <span className="block mx-px bg-zinc-800 rounded-t-sm" style={{ height: `${(wk.stats.planned / maxPlanned) * 36}px` }} />
              </button>
            ) : null)}
          </Row>
          <Row label="Gare" h="h-7">
            {d.competitions.map((c) => (
              <span key={c.id} data-testid={`season-comp-${c.id}`} title={`${c.name} · ${fmt(c.date)}${c.weapon ? " · " + c.weapon : ""}`} className={`absolute top-1 -translate-x-1/2 ${c.technical_priority === "A" ? "text-amber-500" : "text-zinc-600"}`} style={{ left: `${pos(c.date)}%` }}>
                <Trophy className="w-4 h-4" />
              </span>
            ))}
            {d.competitions.length === 0 && <Empty>Nessuna gara collegata</Empty>}
          </Row>
          <div className="relative h-0">
            <span className="absolute -top-[132px] h-[132px] w-px bg-red-500/70" style={{ left: `${pos(new Date().toISOString().slice(0, 10))}%` }} title="Oggi" />
          </div>
        </div>
      </div>
      <p className="text-xs text-zinc-500">Clicca una settimana per aprire il cockpit settimanale. Colore Micro = carico pianificato (Scarico → Alto).</p>
    </section>
  );
}

function Row({ label, h = "h-7", children }) {
  return (
    <div className="flex items-center gap-2">
      <div className="w-14 shrink-0 text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`relative flex-1 ${h} bg-zinc-50 rounded-sm`}>{children}</div>
    </div>
  );
}
function Bar({ style, cls, title, children, testId }) {
  return <div data-testid={testId} title={title} className={`absolute top-0.5 bottom-0.5 rounded-sm px-2 text-[11px] font-semibold truncate leading-6 ${cls}`} style={style}>{children}</div>;
}
function Empty({ children }) { return <span className="absolute inset-0 flex items-center px-2 text-[11px] text-zinc-400">{children}</span>; }
